/**
 * CartNotepad.js
 * Main orchestration layer for cart notepad functionality
 * Coordinates rendering, styling, events, and calculations
 */

import { CartRenderers } from './CartRenderers.js';
import { CartStyles } from './CartStyles.js';
import { CartEvents } from './CartEvents.js';
import { CartCalculations } from './CartCalculations.js';

export class CartNotepad {
  constructor(i18n = null) {
    this.isExpanded = false;
    this.cartItems = [];
    this.expandTimeout = null;
    this.i18n = i18n;
    this.unpaidOrders = [];
    this.mode = 'cart';
    this.orderState = 'cart';
    this.pendingOrder = null;
  }

  /**
   * Show cart notepad with current items
   * @param {Array} items - Current cart items
   * @param {Object} newItem - Newly added item (optional, for highlighting)
   * @param {String} orderState - Current order state ('cart', 'pending-payment', 'completed')
   * @param {Object} pendingOrder - Pending order data if applicable
   */
  show(items = [], newItem = null, orderState = 'cart', pendingOrder = null) {
    this.mode = 'cart';
    console.log('[CartNotepad] Show called with:', {
      itemsCount: items.length,
      newItem: newItem?.name,
      orderState,
      hasPendingOrder: !!pendingOrder
    });

    this.cartItems = items;
    this.orderState = orderState;
    this.pendingOrder = pendingOrder;

    if (orderState === 'pending-payment' && pendingOrder) {
      this.cartItems = items;
      this.unpaidOrders = [pendingOrder];
      if (items.length > 0) {
        this.showCartAndUnpaid(items, [pendingOrder]);
        return;
      }
    }

    this.removeExistingNotepad();
    const notepad = this.createNotepad(newItem);
    this.appendAndAnimate(notepad);
    this.setupAutoHide(4000);
  }

  /**
   * Show unpaid orders list (AFTER_ORDER flow)
   * @param {Array} orders - Array of unpaid orders
   */
  showUnpaidOrders(orders = []) {
    this.unpaidOrders = Array.isArray(orders) ? orders : [];
    this.mode = 'unpaid';

    this.removeExistingNotepad();
    const notepad = this.createUnpaidNotepad();
    this.appendAndAnimate(notepad);
    this.setupAutoHide(8000);
  }

  /**
   * Show combined cart and unpaid orders view
   * @param {Array} items - Cart items
   * @param {Array} unpaidOrders - Unpaid orders
   */
  showCartAndUnpaid(items = [], unpaidOrders = []) {
    this.cartItems = items || [];
    this.unpaidOrders = Array.isArray(unpaidOrders) ? unpaidOrders : [];
    this.mode = 'combined';

    this.removeExistingNotepad();
    const notepad = this.createCombinedNotepad();
    this.appendAndAnimate(notepad);
    this.setupAutoHide(10000);
  }

  /**
   * Show order history when cart is empty
   * @param {Object} historyContent - History content with title, subtitle, action, items
   */
  showOrderHistory(historyContent = {}) {
    this.cartItems = [];

    this.removeExistingNotepad();
    const notepad = this.createOrderHistoryNotepad(historyContent);
    this.appendAndAnimate(notepad);
    this.setupAutoHide(5000);
  }

  /**
   * Hide cart notepad - slide back up
   */
  hide() {
    const notepad = document.querySelector('.cart-notepad');
    if (notepad) {
      notepad.style.transform = 'translateY(-100%)';
      notepad.style.opacity = '0';

      setTimeout(() => {
        if (notepad.parentNode) {
          notepad.remove();
        }
      }, 300);
    }

    this.isExpanded = false;
    if (this.expandTimeout) {
      clearTimeout(this.expandTimeout);
      this.expandTimeout = null;
    }
  }

  // ==================== PRIVATE HELPER METHODS ====================

  /**
   * Remove existing notepad from DOM
   * @private
   */
  removeExistingNotepad() {
    const existing = document.querySelector('.cart-notepad');
    if (existing) {
      console.log('[CartNotepad] Removing existing notepad');
      existing.remove();
    }
  }

  /**
   * Create standard cart notepad element
   * @private
   * @param {Object} newItem - Newly added item (for highlighting)
   * @returns {HTMLElement} Notepad DOM element
   */
  createNotepad(newItem = null) {
    const notepad = document.createElement('div');
    notepad.className = 'cart-notepad';

    const context = {
      mode: this.mode,
      cartItems: this.cartItems,
      pendingOrder: this.pendingOrder,
      orderState: this.orderState,
      i18n: this.i18n,
      newItem
    };

    notepad.innerHTML = CartRenderers.renderNotepad(context);
    CartStyles.styleNotepad(notepad);

    const eventContext = {
      hideCallback: () => this.hide(),
      pendingOrder: this.pendingOrder
    };
    CartEvents.bindEvents(notepad, eventContext);

    return notepad;
  }

  /**
   * Create unpaid orders notepad element
   * @private
   * @returns {HTMLElement} Notepad DOM element
   */
  createUnpaidNotepad() {
    const notepad = document.createElement('div');
    notepad.className = 'cart-notepad unpaid-orders';

    const context = {
      unpaidOrders: this.unpaidOrders,
      i18n: this.i18n
    };

    notepad.innerHTML = CartRenderers.renderUnpaidOrders(context);
    CartStyles.styleNotepad(notepad);

    const eventContext = {
      hideCallback: () => this.hide(),
      unpaidOrders: this.unpaidOrders
    };
    CartEvents.bindUnpaidEvents(notepad, eventContext);

    return notepad;
  }

  /**
   * Create combined cart and unpaid orders notepad element
   * @private
   * @returns {HTMLElement} Notepad DOM element
   */
  createCombinedNotepad() {
    const notepad = document.createElement('div');
    notepad.className = 'cart-notepad combined';

    const context = {
      cartItems: this.cartItems,
      unpaidOrders: this.unpaidOrders,
      i18n: this.i18n
    };

    notepad.innerHTML = CartRenderers.renderNotepad({ ...context, mode: 'combined' });
    CartStyles.styleNotepad(notepad);

    const eventContext = {
      hideCallback: () => this.hide(),
      cartItems: this.cartItems,
      unpaidOrders: this.unpaidOrders
    };
    CartEvents.bindCombinedEvents(notepad, eventContext);

    return notepad;
  }

  /**
   * Create order history notepad element
   * @private
   * @param {Object} historyContent - History content data
   * @returns {HTMLElement} Notepad DOM element
   */
  createOrderHistoryNotepad(historyContent) {
    const notepad = document.createElement('div');
    notepad.className = 'cart-notepad order-history';
    notepad.innerHTML = CartRenderers.renderOrderHistory(historyContent);
    CartStyles.styleNotepad(notepad);

    const eventContext = {
      hideCallback: () => this.hide(),
      pendingOrder: null
    };
    CartEvents.bindEvents(notepad, eventContext);

    return notepad;
  }

  /**
   * Append notepad to DOM and trigger animation
   * @private
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  appendAndAnimate(notepad) {
    document.body.appendChild(notepad);
    console.log('[CartNotepad] Notepad appended to body');

    requestAnimationFrame(() => {
      notepad.style.transform = 'translateY(0)';
      notepad.style.opacity = '1';
      console.log('[CartNotepad] Animation started - transform and opacity applied');
    });

    this.isExpanded = true;
  }

  /**
   * Setup auto-hide timeout
   * @private
   * @param {number} delay - Delay in milliseconds
   */
  setupAutoHide(delay) {
    this.expandTimeout = setTimeout(() => {
      console.log(`[CartNotepad] Auto-hiding after ${delay / 1000} seconds`);
      this.hide();
    }, delay);
  }

  // ==================== LEGACY COMPATIBILITY METHODS ====================
  // These methods are kept for backward compatibility

  /**
   * Format item price (legacy compatibility)
   * @deprecated Use CartCalculations.formatItemPrice instead
   */
  formatItemPrice(item, currency = 'PLN') {
    return CartCalculations.formatItemPrice(item, currency);
  }

  /**
   * Get display total (legacy compatibility)
   * @deprecated Use CartCalculations.getDisplayTotal instead
   */
  getDisplayTotal() {
    return CartCalculations.getDisplayTotal(this.orderState, this.pendingOrder, this.cartItems);
  }
}
